import { useCallback, useEffect, useRef, useState } from 'react'
import { HubConnection } from '@microsoft/signalr'
import { cisgenicTaskApi } from '@/api/config-setup/cisgenicTaskApi'
import { useSignalR } from '@/signalr/context'
import { ActionSignalR } from '@/types/signalr'
import {
  ActiveTaskResponse,
  CisgenicTaskProgress,
  CisgenicTaskStatus,
  CisgenicTaskUpdateEvent,
} from '@/types/cisgenicTask'

// Step name mapping for UI display
const STEP_DISPLAY_NAMES: Record<string, string> = {
  'upload-mk2-decoder': 'Importing MK2 Decoders',
}

// Polling interval in milliseconds (fallback)
const POLLING_INTERVAL = 3000

// SignalR group name pattern
const getTaskGroupName = (interfaceId: number) => `interface:${interfaceId}:tasks`

export interface UseMk2DecoderUploadV2Options {
  interfaceId: number
  onSuccess?: () => void
  onError?: (error: string) => void
  onCancel?: () => void
}

export interface UseMk2DecoderUploadV2Return {
  // Actions
  upload: (file: File) => Promise<void>
  checkActiveTask: () => Promise<ActiveTaskResponse | null>
  cancelTask: () => Promise<boolean>

  // State
  isUploading: boolean
  isCheckingActive: boolean
  isCancelling: boolean
  taskId: string | null
  progress: CisgenicTaskProgress | null
  error: string | null

  // Computed
  currentStepName: string | null
  overallProgress: number
  isCompleted: boolean
  isFailed: boolean
  isCancelled: boolean
}

export const useMk2DecoderUploadV2 = (
  options: UseMk2DecoderUploadV2Options,
): UseMk2DecoderUploadV2Return => {
  const { interfaceId, onSuccess, onError, onCancel } = options
  const { getHubConnect } = useSignalR()

  // State
  const [isUploading, setIsUploading] = useState(false)
  const [isCheckingActive, setIsCheckingActive] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [taskId, setTaskId] = useState<string | null>(null)
  const [progress, setProgress] = useState<CisgenicTaskProgress | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Refs
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const maxProgressRef = useRef<number>(0)
  const onSuccessRef = useRef(onSuccess)
  const onErrorRef = useRef(onError)
  const onCancelRef = useRef(onCancel)
  const connectionRef = useRef<HubConnection | null>(null)
  const joinedGroupRef = useRef<string | null>(null)
  const currentTaskIdRef = useRef<string | null>(null)

  // Keep refs updated with latest callbacks
  useEffect(() => {
    onSuccessRef.current = onSuccess
    onErrorRef.current = onError
    onCancelRef.current = onCancel
  }, [onSuccess, onError, onCancel])

  /**
   * Clear polling interval
   */
  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  /**
   * Cleanup SignalR listener
   */
  const cleanupSignalR = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.off(ActionSignalR.CisgenicTaskUpdate)
      if (joinedGroupRef.current) {
        connectionRef.current
          .invoke('LeaveGroup', joinedGroupRef.current)
          .catch(() => {})
        joinedGroupRef.current = null
      }
    }
  }, [])

  /**
   * Handle task completion/failure
   */
  const handleTaskComplete = useCallback(
    (status: CisgenicTaskStatus, errorMessage?: string | null) => {
      clearPolling()
      cleanupSignalR()
      setIsUploading(false)

      if (status === CisgenicTaskStatus.Completed) {
        onSuccessRef.current?.()
      } else if (status === CisgenicTaskStatus.Failed) {
        const errorMsg = errorMessage || 'Upload failed'
        setError(errorMsg)
        onErrorRef.current?.(errorMsg)
      }
    },
    [clearPolling, cleanupSignalR],
  )

  /**
   * Start HTTP polling (fallback when SignalR fails)
   */
  const startPolling = useCallback(
    (currentTaskId: string) => {
      clearPolling()

      pollingIntervalRef.current = setInterval(async () => {
        try {
          const { data } = await cisgenicTaskApi.getTaskProgress(currentTaskId)

          const effectiveProgress = Math.max(
            data.progress,
            maxProgressRef.current,
          )
          maxProgressRef.current = effectiveProgress

          setProgress({
            ...data,
            progress: effectiveProgress,
          })

          if (
            data.status === CisgenicTaskStatus.Completed ||
            data.status === CisgenicTaskStatus.Failed
          ) {
            handleTaskComplete(data.status, data.errorMessage)
          }
        } catch (err) {
          // Ignore polling errors
        }
      }, POLLING_INTERVAL)
    },
    [clearPolling, handleTaskComplete],
  )

  /**
   * Setup SignalR listener (primary method)
   * Falls back to polling if SignalR fails
   */
  const setupSignalR = useCallback(
    async (currentTaskId: string) => {
      currentTaskIdRef.current = currentTaskId

      try {
        const connection = await getHubConnect()
        connectionRef.current = connection

        // Join group
        const groupName = getTaskGroupName(interfaceId)
        await connection.invoke('JoinGroup', groupName)
        joinedGroupRef.current = groupName
        console.log('[MK2 SignalR] Joined group:', groupName)

        // Listen for task updates
        const taskUpdateHandler = async (event: CisgenicTaskUpdateEvent) => {
          // Debug: Log SignalR event for production debugging
          console.log('[SignalR MK2 Decoder] Event received:', {
            taskId: event.taskId,
            status: event.status,
            progress: event.progress,
            currentStep: event.currentStep,
            stepProgress: event.stepProgress,
            message: event.message,
            errorMessage: event.errorMessage,
          })

          if (event.taskId !== currentTaskIdRef.current) {
            return
          }

          // Update progress
          const effectiveProgress = Math.max(
            event.progress,
            maxProgressRef.current,
          )
          maxProgressRef.current = effectiveProgress

          setProgress((prev) => ({
            ...prev,
            taskId: event.taskId,
            status: event.status,
            progress: effectiveProgress,
            currentStep: event.currentStep,
            steps: event.steps || prev?.steps || [],
            errorMessage: event.errorMessage,
            createdAt: prev?.createdAt || '',
            startedAt: prev?.startedAt || null,
            completedAt: prev?.completedAt || null,
            estimatedTimeRemaining: event.estimatedTimeRemaining || null,
          }))

          // Check completion
          if (
            event.status === CisgenicTaskStatus.Completed ||
            event.status === CisgenicTaskStatus.Failed
          ) {
            handleTaskComplete(event.status, event.errorMessage)
          }
        }

        connection.on(ActionSignalR.CisgenicTaskUpdate, taskUpdateHandler)

        // Handle connection close - fallback to polling
        connection.onclose(() => {
          if (currentTaskIdRef.current) {
            startPolling(currentTaskIdRef.current)
          }
        })
      } catch (err) {
        // SignalR failed, fallback to polling
        startPolling(currentTaskId)
      }
    },
    [getHubConnect, interfaceId, handleTaskComplete, startPolling],
  )

  /**
   * Check if there's an active upload task
   */
  const checkActiveTask =
    useCallback(async (): Promise<ActiveTaskResponse | null> => {
      setIsCheckingActive(true)
      setError(null)

      try {
        const { data } =
          await cisgenicTaskApi.getActiveMk2DecoderTask(interfaceId)

        if (data.hasActiveTask && data.taskId) {
          setTaskId(data.taskId)

          try {
            const progressResponse = await cisgenicTaskApi.getTaskProgress(
              data.taskId,
            )
            setProgress(progressResponse.data)

            if (
              progressResponse.data.status === CisgenicTaskStatus.Processing ||
              progressResponse.data.status === CisgenicTaskStatus.Pending
            ) {
              setIsUploading(true)
              await setupSignalR(data.taskId)
              return data
            }
          } catch (err) {
            // Failed to get task progress
          }
        }

        setIsCheckingActive(false)
        return data
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.message || 'Failed to check active task'
        setError(errorMsg)
        setIsCheckingActive(false)
        return null
      }
    }, [interfaceId, setupSignalR])

  /**
   * Poll active task endpoint until hasActiveTask is false
   */
  const pollUntilNoActiveTask = useCallback(async (): Promise<boolean> => {
    const POLL_INTERVAL = 2000
    const MAX_WAIT_TIME = 60000
    const startTime = Date.now()

    while (Date.now() - startTime < MAX_WAIT_TIME) {
      try {
        const { data } = await cisgenicTaskApi.getActiveMk2DecoderTask(interfaceId)

        if (!data.hasActiveTask) {
          return true
        }
      } catch {
        // Continue polling on error
      }

      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
    }

    // Timeout reached
    return false
  }, [interfaceId])

  /**
   * Cancel the current task
   * After cancelling, polls active endpoint until no active task found
   */
  const cancelTask = useCallback(async (): Promise<boolean> => {
    if (!taskId) {
      return false
    }

    setIsCancelling(true)

    try {
      const { data } = await cisgenicTaskApi.cancelTask(taskId)

      // API returns { message: "Task cancelled successfully" } on success
      // Check for success message or absence of error
      if (data.message?.toLowerCase().includes('cancelled successfully') || data.success) {
        clearPolling()
        cleanupSignalR()

        // Poll until no active task
        await pollUntilNoActiveTask()

        setIsUploading(false)
        setIsCancelling(false)
        onCancelRef.current?.()
        return true
      }

      setIsCancelling(false)
      return false
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message || 'Failed to cancel task'
      setError(errorMsg)
      setIsCancelling(false)
      return false
    }
  }, [taskId, clearPolling, cleanupSignalR, pollUntilNoActiveTask])

  /**
   * Upload file and start tracking
   */
  const upload = useCallback(
    async (file: File) => {
      setIsUploading(true)
      setError(null)
      setTaskId(null)
      setProgress(null)
      maxProgressRef.current = 0

      try {
        const formData = new FormData()
        formData.append('file', file)

        const { data } = await cisgenicTaskApi.uploadMk2DecoderV2(
          interfaceId,
          formData,
        )

        if (!data.success || !data.taskId) {
          throw new Error(data.message || 'Upload failed')
        }

        setTaskId(data.taskId)
        await setupSignalR(data.taskId)
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.message || err?.message || 'Upload failed'
        setError(errorMsg)
        setIsUploading(false)
        onErrorRef.current?.(errorMsg)
      }
    },
    [interfaceId, setupSignalR],
  )

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearPolling()
      cleanupSignalR()
      currentTaskIdRef.current = null
    }
  }, [clearPolling, cleanupSignalR])

  // Computed values
  const currentStepName = progress?.currentStep
    ? STEP_DISPLAY_NAMES[progress.currentStep] || progress.currentStep
    : null

  const overallProgress = progress?.progress || 0

  const isCompleted = progress?.status === CisgenicTaskStatus.Completed

  const isFailed = progress?.status === CisgenicTaskStatus.Failed

  const isCancelled = progress?.status === CisgenicTaskStatus.Cancelled

  return {
    // Actions
    upload,
    checkActiveTask,
    cancelTask,

    // State
    isUploading,
    isCheckingActive,
    isCancelling,
    taskId,
    progress,
    error,

    // Computed
    currentStepName,
    overallProgress,
    isCompleted,
    isFailed,
    isCancelled,
  }
}

export default useMk2DecoderUploadV2
